import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { DatabaseService } from '../../../services/database.service';
import { LogService } from '../../../services/Logging.service';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../services/global-environment';
import { HttpClient } from '@angular/common/http';
import { v4 as uuidv4 } from "uuid";
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { I18nService } from '../../../services/i18n.service';
import { OrganizationTreeNodeComponent } from './organization-tree-node/organization-tree-node.component';
import { OrganizationManagementTreeNodeComponent } from './organization-management-tree-node/organization-management-tree-node.component';

// 引用資料庫模型定義
import { User, UserModule } from '../../../../database_model/database_model';

// API 回應介面
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// AI 助手介面
interface Assistant {
  assistantNo: string;
  title: string;
  description?: string;
  icon?: string;
}

// 組織節點介面
interface OrganizationNode {
  id: string;
  name: string;
  type: 'company' | 'department' | 'user';
  children?: OrganizationNode[];
  role?: string;
  companyid?: string;
  assistantNo?: string;
  expanded?: boolean; // 用於樹狀結構展開/收合
  isMoving?: boolean; // 用於視覺反饋
}

// 帶有層級資訊的部門節點（用於下拉選單顯示）
interface DepartmentWithLevel {
  id: string;
  name: string;
  level: number;
  type: 'company' | 'department';
}

// 組織管理回應介面
interface OrganizationResponse {
  companyid: string;
  assistantNo: string;
  id: string;
  name: string;
  type: string;
  children: OrganizationNode[];
}



@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    OrganizationTreeNodeComponent,
    OrganizationManagementTreeNodeComponent
  ],
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss'
})
export class UserManagementComponent implements OnInit {
  users: User[] = [];
  filteredUsers: User[] = []; // 新增：過濾後的使用者列表
  assistants: Assistant[] = [];
  userForm: FormGroup;
  isEditing = false;
  isLoading = false;
  errorMessage = '';
  selectedUser: User | null = null;
  showModal = false; // 控制彈出視窗顯示

  // 組織管理相關屬性
  organizationData: OrganizationNode | null = null;
  selectedDepartment: OrganizationNode | null = null;
  isOrgPanelCollapsed = false; // 新增：組織架構面板收合狀態

  // 組織架構設定相關屬性
  showOrgSettingsModal = false; // 控制組織設定彈出視窗
  orgEditingNode: OrganizationNode | null = null; // 正在編輯的節點
  orgEditMode: 'add' | 'edit' | 'delete' | null = null; // 編輯模式
  orgFormData = {
    name: '',
    parentId: '',
    type: 'department' as 'department' | 'company'
  };

  // 診斷模式，可以在控制台輸出更多信息
  debugMode = false; // 可以在瀏覽器控制台設置為true以顯示詳細錯誤信息

  // 用戶數據模型，用於模板驅動表單，使用 User 介面
  userData: User = {} as User;

  // 額外屬性用於權限設定UI
  permissionType: 'all' | 'individual' = 'all';
  allAssistantsPermission: string = 'admin';

  assistantNo = "ALL";

  // 角色選項定義
  roleOptions = [
    { label: 'role.super_admin', value: 'super_admin' },
    { label: 'role.admin', value: 'admin' },
    { label: 'role.user', value: 'user' }
  ];

  // 部門成員管理相關屬性
  showDepartmentMemberModal = false;
  selectedUserToAdd = ''; // 改為單一選擇的用戶ID
  selectedUsersToAdd: User[] = [];
  userFilterType: 'all' | 'noDepartment' = 'all'; // 用戶篩選類型

  // 新增：使用者選擇相關屬性
  selectedUsersForRemoval: Set<string> = new Set(); // 選中要移除的用戶ID
  isSelectAllChecked = false; // 全選狀態

  constructor(
    private databaseService: DatabaseService,
    private logService: LogService,
    private fb: FormBuilder,
    private http: HttpClient,
    private i18nService: I18nService
  ) {
    this.userForm = this.createUserForm();
  }

  ngOnInit(): void {
    this.isLoading = true;

    // 同時加載組織數據、用戶和助手資料
    forkJoin({
      organization: this.loadOrganizationData(),
      users: this.loadUsers(),
      assistants: this.loadAssistants()
    }).subscribe({
      next: (data) => {
        this.organizationData = data.organization;
        // 初始化組織樹的展開狀態
        if (this.organizationData) {
          this.initializeOrgNodeExpandedStates(this.organizationData);
        }

        console.log('組織資料:', this.organizationData);

        this.users = data.users;
        this.filteredUsers = [...this.users]; // 初始化過濾列表
        this.assistants = data.assistants;

        // 補充組織架構中用戶節點的完整資訊
        if (this.organizationData && this.users.length > 0) {
          console.log('開始處理組織架構用戶資訊...');
          this.cleanupOrganizationData();
          this.enrichOrganizationWithUserInfo(this.organizationData);
          this.validateDataConsistency();
          console.log('處理完成後的組織資料:', this.organizationData);

          // 統計各部門的用戶數量
          this.logDepartmentUserCounts(this.organizationData);
        }

        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = this.i18nService.translate('user_management.errors.load_data_failed');
        this.isLoading = false;
        console.error('Error loading data:', error);
      }
    });
  }

  // 載入組織資料 - 使用真實 API
  private loadOrganizationData(): Observable<OrganizationNode | null> {
    const companyid = environment.companyid;
    const companyname = environment.companyname;
    const assistantNo = this.assistantNo;


    const url = `${environment.apiUrl}/api/departments/${companyid}/${assistantNo}`;

    // 添加 companyname 查詢參數
    const params = { companyname };

    console.log('載入組織資料，URL:', url, '參數:', params);

    return this.http.get<ApiResponse<OrganizationNode>>(url, { params }).pipe(
      map((response) => {
        console.log('組織資料 API 回應:', response);
        if (response.success && response.data) {
          // 確保回應的資料有 expanded 屬性
          if (response.data.type === 'company') {
            (response.data as any).expanded = true;
          }
          return response.data;
        }
        return null;
      }),
      catchError(error => {
        console.error('載入組織資料失敗:', error);
        // API 失敗時返回 null，不再使用 Mock 資料
        return of(null);
      })
    );
  }

  // 補充組織架構中用戶節點的完整資訊
  private enrichOrganizationWithUserInfo(node: OrganizationNode): void {
    if (!node) return;

    // 如果是用戶節點且有ID，補充用戶資訊
    if (node.type === 'user' && node.id) {
      const user = this.users.find(u => u.id === node.id);
      if (user) {
        // 補充用戶名稱和其他資訊
        node.name = user.fullName || user.username || user.email || `用戶 ${node.id}`;
        node.companyid = user.companyid || environment.companyid;
        console.log(`補充用戶資訊: ${node.name} (ID: ${node.id})`);
      } else {
        // 記錄無效用戶 ID
        console.warn(`無效的用戶 ID: ${node.id} 在組織架構中找到但用戶列表中不存在`);
        node.name = `未知用戶 (${node.id})`;
      }
    }

    // 遞歸處理所有子節點，並過濾掉無效的用戶
    if (node.children && node.children.length > 0) {
      node.children = node.children.map(child => {
        if (child.type === 'user') {
          // 驗證用戶是否存在於用戶列表中
          const userExists = this.users.some(user => user.id === child.id);
          if (userExists) {
            this.enrichOrganizationWithUserInfo(child);
            return child;
          } else {
            // 記錄無效用戶 ID 並返回 null
            console.warn(`無效的用戶 ID: ${child.id} 在組織架構中找到但用戶列表中不存在`);
            return null;
          }
        } else if (child.type === 'department') {
          this.enrichOrganizationWithUserInfo(child);
          return child;
        }
        return child;
      }).filter(child => child !== null) as OrganizationNode[]; // 過濾掉無效的用戶
    }
  }

  // 統計並記錄各部門的用戶數量
  private logDepartmentUserCounts(node: OrganizationNode, level: number = 0): void {
    if (!node) return;

    const indent = '  '.repeat(level);

    if (node.type === 'department') {
      const userCount = this.countUsersInDepartment(node);
      console.log(`${indent}${node.name}: ${userCount} 位用戶`);
    }

    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        this.logDepartmentUserCounts(child, level + 1);
      });
    }
  }

  // 計算部門直接包含的用戶數量（不包括子部門的用戶）
  private countUsersInDepartment(node: OrganizationNode): number {
    if (!node || !node.children) return 0;

    return node.children.filter(child => child.type === 'user').length;
  }

  // 幫助診斷表單驗證錯誤的方法
  getFormValidationErrors(): string {
    if (!this.userForm) return '';

    const errors: string[] = [];

    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      if (control && control.invalid) {
        if (control instanceof FormArray) {
          errors.push(`${key}: FormArray有${control.length}項，可能有項目未正確設定`);
          if (control.length > 0) {
            control.controls.forEach((item, index) => {
              if (item instanceof FormGroup) {
                Object.keys(item.controls).forEach(itemKey => {
                  const itemControl = item.get(itemKey);
                  if (itemControl && itemControl.invalid) {
                    errors.push(`${key}[${index}].${itemKey}: ${JSON.stringify(itemControl.errors)}`);
                  }
                });
              }
            });
          }
        } else {
          errors.push(`${key}: ${JSON.stringify(control.errors)}`);
        }
      }
    });

    return errors.join('\n');
  }

  // 打開用戶彈出視窗
  openUserModal() {
    this.isEditing = false;
    this.userForm = this.createUserForm();
    this.resetUserData();

    // 確保在 "all" 模式下清空權限數組
    const permissions = this.userForm.get('permissions') as FormArray;
    if (this.userForm.get('permissionType')?.value === 'all') {
      while (permissions.length > 0) {
        permissions.removeAt(0);
      }
    }

    console.log('表單狀態:', this.userForm.valid ? '有效' : '無效');
    console.log('表單錯誤:', this.getFormValidationErrors());

    this.showModal = true;
  }

  // 關閉用戶彈出視窗
  closeUserModal() {
    this.showModal = false;
    this.isEditing = false;
    this.selectedUser = null;
  }

  // 編輯用戶時打開彈出視窗
  editUser(user: User): void {
    this.isEditing = true;
    this.selectedUser = user;

    // 判斷使用者的權限設定方式
    let permissionType = 'individual';
    let allAssistantsPermission = 'admin';

    if (user.modules && user.modules.length === 1 && user.modules[0].assistantNo === 'ALL') {
      permissionType = 'all';
      allAssistantsPermission = user.modules[0].permission;
    }

    // 創建表單
    this.userForm = this.createUserForm(user, permissionType, allAssistantsPermission);

    // 如果是個別設定，添加現有權限
    if (permissionType === 'individual' && user.modules && user.modules.length > 0) {
      // 清除默認的權限
      while (this.permissionsArray.length) {
        this.permissionsArray.removeAt(0);
      }

      // 添加現有權限
      user.modules.forEach(module => {
        this.addPermissionWithValue(module.assistantNo, module.permission);
      });
    }

    // 編輯用戶時填充用戶數據
    this.fillUserData(user);

    this.showModal = true;
  }

  // 創建用戶表單
  createUserForm(user?: User, permissionType: string = 'all', allAssistantsPermission: string = 'admin'): FormGroup {
    return this.fb.group({
      id: [user?.id || ''],
      username: [user?.username || '', [Validators.required, Validators.minLength(3)]],
      password: [user?.password || '', user ? [] : [Validators.required, Validators.minLength(6)]],
      email: [user?.email || '', [Validators.email]],  // 秼移除必填驗證
      fullName: [user?.fullName || ''],  // 秼移除必填驗證
      department: [user?.department || ''],
      position: [user?.position || ''],
      enabled: [user?.enabled !== undefined ? user.enabled : true],
      permissionType: [permissionType],
      allAssistantsPermission: [allAssistantsPermission],
      permissions: this.fb.array(user?.modules ?
        user.modules.map(p => this.createPermissionGroup(p)) :
        []
      )
    });
  }

  // 創建權限表單組
  createPermissionGroup(permission?: any): FormGroup {
    return this.fb.group({
      assistantNo: [permission?.assistantNo || '', Validators.required],
      permission: [permission?.permission || 'admin', Validators.required]
    });
  }

  // 重置用戶數據為初始狀態
  resetUserData(): void {
    this.userData = {} as User;

    this.permissionType = 'all';
    this.allAssistantsPermission = 'admin';

    // 初始化 userData 中的權限相關屬性
    this.userData.permissionType = 'all';
    this.userData.allAssistantsPermission = 'admin';
  }

  // 填充用戶數據到表單
  fillUserData(user: User): void {
    this.userData = {
      ...user,
      password: '', // 不填充密碼
    };

    // 判斷使用者的權限設定方式
    if (user.modules && user.modules.length === 1 && user.modules[0].assistantNo === 'ALL') {
      this.permissionType = 'all';
      this.userData.permissionType = 'all';
      this.allAssistantsPermission = user.modules[0].permission;
      this.userData.allAssistantsPermission = user.modules[0].permission;
      // 統一權限時，modules 可以為空或只保留 ALL
      this.userData.modules = [];
    } else {
      this.permissionType = 'individual';
      this.userData.permissionType = 'individual';
      // 個別權限時，複製 modules
      this.userData.modules = user.modules ? user.modules.map(m => ({ ...m })) : [];
      // 如果沒有權限模組，添加一個空的權限
      if (!this.userData.modules || this.userData.modules.length === 0) {
        this.userData.modules = [];
        this.addPermission();
      }
    }
  }

  // 當權限設定方式改變時
  onPermissionTypeChange(): void {
    // 同步 class 層級的 permissionType
    this.permissionType = this.userData.permissionType;

    if (this.permissionType === 'all') {
      // 清除個別權限設定
      this.userData.modules = [];
      // 確保 userData.allAssistantsPermission 有初始值
      if (!this.userData.allAssistantsPermission) {
        this.userData.allAssistantsPermission = this.allAssistantsPermission;
      }
    } else if (this.permissionType === 'individual' && (!this.userData.modules || this.userData.modules.length === 0)) {
      // 添加至少一個個別權限設定
      this.addPermission();
    }
  }

  // 添加新權限
  addPermission(): void {
    if (this.userData.permissionType !== 'individual') {
      // 僅在個別AI助手設定時允許新增權限
      return;
    }
    if (!this.userData.modules) {
      this.userData.modules = [];
    }

    this.userData.modules.push({
      assistantNo: '',
      permission: 'admin'
    });
  }

  // 移除權限
  removePermission(index: number): void {
    if (this.userData.permissionType !== 'individual') {
      // 僅在個別AI助手設定時允許移除權限
      return;
    }
    if (this.userData.modules && this.userData.modules.length > index) {
      this.userData.modules.splice(index, 1);
    }
  }

  // 載入用戶資料
  loadUsers(): Observable<User[]> {
    const companyid = environment.companyid;
    const filter = { companyid };

    return this.databaseService.getDataByQuery(filter, 1000, "users").pipe(
      map((response: any) => {
        if (!response?.items) {
          return [];
        }

        console.log('載入的用戶資料:', response.items);

        return response.items.map((item: User) => ({
          id: item.id,
          username: item.username,
          password: item.password || '',
          email: item.email || '',
          companyid: item.companyid,
          fullName: item.fullName || '',
          department: item.department || '',
          position: item.position || '',
          enabled: item.enabled !== undefined ? item.enabled : true,
          modules: item.modules || [],
          createdAt: item.createdAt ? new Date(item.createdAt) : undefined,
          lastLogin: item.lastLogin ? new Date(item.lastLogin) : undefined,
          _rid: item._rid,
          _self: item._self,
          _etag: item._etag,
          _attachments: item._attachments,
          _ts: item._ts
        } as User));
      }),
      catchError(error => {
        console.error('Error loading users:', error);
        return of([]);
      })
    );
  }

  // 載入 AI 助手資料
  loadAssistants(): Observable<Assistant[]> {
    const companyid = environment.companyid;
    const filter = { companyid };

    return this.databaseService.getDataByQuery(filter, 1000, "modules").pipe(
      map((response: any) => {
        if (!response?.items?.[0]?.lobby) {
          return [];
        }

        return response.items[0].lobby.map((item: any) => ({
          assistantNo: item.assistantNo,
          title: item.title,
          description: item.description || '',
          icon: item.icon || ''
        }));
      }),
      catchError(error => {
        console.error('Error loading assistants:', error);
        return of([]);
      })
    );
  }

  // 提交用戶表單
  submitUserForm(): void {
    console.log('嘗試提交表單');
    console.log('userData:', this.userData);
    console.log('permissionType:', this.permissionType);
    console.log('allAssistantsPermission (class level):', this.allAssistantsPermission);
    console.log('userData.allAssistantsPermission:', this.userData.allAssistantsPermission);

    // 直接檢查 userData 是否有效，而不是檢查 ReactiveForm
    if (!this.isValidForm()) {
      this.errorMessage = this.i18nService.translate('user_management.errors.required_fields');
      return;
    }

    // 準備提交數據
    const userData = this.prepareUserData();
    console.log('準備提交的數據:', userData);
    console.log('最終 modules:', userData.modules);

    if (this.isEditing) {
      this.updateExistingUser(userData);
    } else {
      this.createNewUser(userData);
    }
  }

  // 檢查表單是否有效
  isValidForm(): boolean {
    // 直接檢查 userData 必填欄位
    if (!this.userData.username || this.userData.username.length < 3) {
      this.errorMessage = this.i18nService.translate('user_management.errors.username_min_length');
      return false;
    }

    // 如果是新用戶，檢查密碼
    if (!this.isEditing && (!this.userData.password || this.userData.password.length < 6)) {
      this.errorMessage = this.i18nService.translate('user_management.errors.password_min_length');
      return false;
    }

    // 如果設置了電子郵件，檢查格式
    if (this.userData.email && !/\S+@\S+\.\S+/.test(this.userData.email)) {
      this.errorMessage = this.i18nService.translate('user_management.errors.invalid_email');
      return false;
    }

    // 檢查個別權限設定
    if (this.permissionType === 'individual') {
      if (!this.userData.modules || this.userData.modules.length === 0) {
        this.errorMessage = this.i18nService.translate('user_management.errors.min_permission_required');
        return false;
      }

      // 確保每個權限都有選擇助手
      for (const perm of this.userData.modules) {
        if (!perm.assistantNo) {
          this.errorMessage = '請為每個權限選擇AI助手';
          return false;
        }
      }
    }

    // 所有驗證通過
    this.errorMessage = '';
    return true;
  }

  // 準備用戶數據
  prepareUserData(): User {
    // 創建一個不修改原始對象的副本
    const processedData: User = {
      ...this.userData
    };

    // 處理權限設定
    if (this.permissionType === 'all') {
      // 所有AI助手統一設定
      processedData.modules = [{
        assistantNo: 'ALL',
        permission: this.userData.allAssistantsPermission
      }];
    } else {
      // 個別AI助手設定
      // 確保此處是從 userData 中獲取已經設定的個別權限
      if (!processedData.modules || processedData.modules.length === 0) {
        this.errorMessage = '請至少選擇一個 AI 助手的權限';
        throw new Error('未選擇任何 AI 助手權限');
      }

      // 確保每個模組都有有效的 assistantNo
      const invalidModules = processedData.modules.filter(m => !m.assistantNo || m.assistantNo === 'ALL');
      if (invalidModules.length > 0) {
        this.errorMessage = '有一些權限設定無效，請檢查';
        throw new Error('AI 助手權限設定無效');
      }
    }

    // 如果是編輯模式且密碼為空，則創建一個新對象，不包含密碼屬性
    if (this.isEditing && !processedData.password) {
      const { password, ...userDataWithoutPassword } = processedData;
      return userDataWithoutPassword as User;
    }

    return processedData;
  }

  // 創建新用戶
  createNewUser(userData: User): void {
    console.log('Creating user with data:', userData);

    // 確保必要欄位存在
    const companyid = environment.companyid;
    userData.companyid = companyid;
    userData.createdAt = new Date();

    // 確保 modules 陣列不為空
    if (!userData.modules || userData.modules.length === 0) {
      this.errorMessage = 'Modules 不能為空，請設定用戶權限';
      return;
    }

    // 在提交前再次確認個別助手權限的設定是否正確
    if (this.permissionType === 'individual') {
      // 檢查是否存在有 ALL 的項目，如果有就移除它
      userData.modules = userData.modules.filter(m => m.assistantNo !== 'ALL');

      // 檢查是否有任何個別權限
      if (userData.modules.length === 0) {
        this.errorMessage = '請至少選擇一個 AI 助手的權限';
        return;
      }

      // 檢查是否所有 assistantNo 都有效
      const invalidModules = userData.modules.some(m => !m.assistantNo || m.assistantNo.trim() === '');
      if (invalidModules) {
        this.errorMessage = '部分 AI 助手權限無效，請重新設定';
        return;
      }
    }


    console.log('提交用戶數據:', userData);


    this.isLoading = true;

    // 直接呼叫 API 創建用戶
    const apiUrl = `${environment.apiUrl}/api/users/adduser`;

    // 使用 fetch API 直接呼叫
    fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData)
    })
    .then(response => {
      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('此用戶名已存在，請使用其他用戶名');
        } else if (response.status === 400) {
          throw new Error('缺少必要參數，請確保所有必填欄位都已填寫');
        } else {
          throw new Error(`服務器錯誤 (${response.status})`);
        }
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {

        alert(this.i18nService.translate('user_management.messages.user_created_success'));

        // 重新加載用戶列表
        this.loadUsers().subscribe(users => {
          this.users = users;
          this.isLoading = false;
          this.closeUserModal();
        });
      } else {
        throw new Error(data.message || '創建用戶失敗');
      }
    })
    .catch(error => {
      console.error('Error creating user:', error);
      this.errorMessage = error.message || '創建用戶失敗';
      alert(this.errorMessage);
      this.isLoading = false;
    });
  }

  // 更新用戶
  updateExistingUser(userData: any): void {
    const companyid = environment.companyid;
    userData.companyid = companyid;

    // 如果密碼欄位為空，表示不需要更改密碼
    if (!userData.password) {
      const currentUser = this.users.find(u => u.id === userData.id);
      if (currentUser) {
        userData.password = currentUser.password;
      }
    }

    // 移除 createdAt，避免後端報錯
    if (userData.createdAt) {
      delete userData.createdAt;
    }
    // 添加 updatedAt
    userData.updatedAt = new Date().toISOString();

    // 檢查必要參數
    if (!userData.id || !companyid) {
      this.errorMessage = '缺少必要參數：用戶ID或公司ID';
      alert(this.errorMessage);
      return;
    }

    // 檢查 modules 不能為空
    if (!userData.modules || !Array.isArray(userData.modules) || userData.modules.length === 0) {
      this.errorMessage = 'modules 不能為空，請設定用戶權限';
      alert(this.errorMessage);
      return;
    }

    this.isLoading = true;
    const apiUrl = `${environment.apiUrl}/api/users/updateuser?id=${encodeURIComponent(userData.id)}&companyid=${encodeURIComponent(companyid)}`;

    fetch(apiUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData)
    })
      .then(async response => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          let msg = errorData.message || `伺服器錯誤 (${response.status})`;
          throw new Error(msg);
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          alert(this.i18nService.translate('user_management.messages.user_updated_success'));
          // 重新加載用戶列表
          this.loadUsers().subscribe(users => {
            this.users = users;
            this.isLoading = false;
            this.closeUserModal();
          });
        } else {
          throw new Error(data.message || '用戶更新失敗');
        }
      })
      .catch(error => {
        console.error('Error updating user:', error);
        this.errorMessage = error.message || '用戶更新失敗';
        alert(this.errorMessage);
        this.isLoading = false;
      });
  }

  // 刪除用戶
  deleteUser(user: User): void {
    const confirmMessage = this.i18nService.translate('user_management.confirm_delete_user', { username: user.username });
    if (!confirm(confirmMessage)) {
      return;
    }

    const companyid = environment.companyid;

    this.isLoading = true;

    // 檢查必要參數
    if (!user.id || !companyid) {
      this.errorMessage = '無法刪除用戶：缺少用戶ID或公司ID';
      this.isLoading = false;
      alert(this.errorMessage);
      return;
    }

    // 構建 API URL 和查詢參數
    const apiUrl = `${environment.apiUrl}/api/users/deleteuser`;
    const queryParams = `?id=${encodeURIComponent(user.id)}&companyid=${encodeURIComponent(companyid)}`;

    // 直接呼叫 API
    fetch(`${apiUrl}${queryParams}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    .then(response => {
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('找不到該用戶，可能已被刪除');
        } else if (response.status === 400) {
          throw new Error('請求參數無效');
        } else {
          throw new Error(`服務器錯誤 (${response.status})`);
        }
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        // 更新本地用戶列表，移除已刪除用戶
        this.users = this.users.filter(u => u.id !== user.id);

        // 記錄刪除操作
        this.logService.Loginlog(
          companyid,
          'UserManagement',
          'Admin',
          `Deleted user: ${user.username}`,
          'INFO'
        );

        alert(this.i18nService.translate('user_management.messages.user_deleted_success'));
      } else {
        throw new Error(data.message || '刪除用戶失敗');
      }
    })
    .catch(error => {
      console.error('Error deleting user:', error);
      this.errorMessage = error.message || '刪除用戶時發生錯誤';
      alert(this.errorMessage);
    })
    .finally(() => {
      this.isLoading = false;
    });
  }

  // 獲取助手名稱
  getAssistantName(assistantNo: string): string {
    if (assistantNo === 'ALL') {
      return '所有助手';
    }
    const assistant = this.assistants.find(a => a.assistantNo === assistantNo);
    return assistant ? assistant.title : assistantNo;
  }

  // 根據角色值獲取顯示標籤
  getRoleName(roleValue: string): string {
    const role = this.roleOptions.find(option => option.value === roleValue);
    return role ? this.i18nService.translate(role.label) : roleValue;
  }

  // 獲取用戶角色顯示文字
  getUserRoleDisplay(user: any): string {
    if (!user.modules || user.modules.length === 0) {
      return this.i18nService.translate('user_management.role.not_set');
    }

    // 如果是統一權限設定 (ALL)
    if (user.modules.length === 1 && user.modules[0].assistantNo === 'ALL') {
      return this.getRoleName(user.modules[0].permission);
    }

    // 如果是個別權限設定，顯示第一個權限或者混合權限
    const permissions = user.modules.map((m: any) => m.permission as string);
    const uniquePermissions = [...new Set(permissions)];

    if (uniquePermissions.length === 1) {
      return this.getRoleName(uniquePermissions[0] as string);
    } else {
      return this.i18nService.translate('user_management.role.mixed_permissions');
    }
  }

  // 獲取用戶角色的 CSS 類別
  getUserRoleClass(user: any): string {
    if (!user.modules || user.modules.length === 0) {
      return 'role-none';
    }

    let mainRole = '';

    // 如果是統一權限設定 (ALL)
    if (user.modules.length === 1 && user.modules[0].assistantNo === 'ALL') {
      mainRole = user.modules[0].permission;
    } else {
      // 個別權限設定，取最高權限
      const permissions = user.modules.map((m: any) => m.permission);
      if (permissions.includes('super_admin')) {
        mainRole = 'super_admin';
      } else if (permissions.includes('admin')) {
        mainRole = 'admin';
      } else {
        mainRole = 'user';
      }
    }

    return `role-${mainRole}`;
  }

  // 獲取用戶可用 Assistant 數目顯示文字
  getAssistantCountDisplay(user: any): string {
    if (!user.modules || user.modules.length === 0) {
      return '0';
    }

    // 如果是統一權限設定 (ALL)
    if (user.modules.length === 1 && user.modules[0].assistantNo === 'ALL') {
      return 'ALL';
    }

    // 個別權限設定，返回實際數量
    return user.modules.length.toString();
  }

  // 獲取 Assistant 數目的 CSS 類別
  getAssistantCountClass(user: any): string {
    if (!user.modules || user.modules.length === 0) {
      return 'count-none';
    }

    // 如果是統一權限設定 (ALL)
    if (user.modules.length === 1 && user.modules[0].assistantNo === 'ALL') {
      return 'count-all';
    }

    // 個別權限設定，根據數量返回不同類別
    const count = user.modules.length;
    if (count >= 5) {
      return 'count-many';
    } else if (count >= 3) {
      return 'count-medium';
    } else {
      return 'count-few';
    }
  }

  // 將表單所有欄位標記為已觸碰，顯示驗證錯誤
  markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(c => {
          if (c instanceof FormGroup) {
            this.markFormGroupTouched(c);
          }
        });
      }
    });
  }

  get permissionsArray(): FormArray {
    return this.userForm.get('permissions') as FormArray;
  }

  // 添加指定權限值的新權限
  addPermissionWithValue(assistantNo: string, permission: string): void {
    const permissionGroup = this.fb.group({
      assistantNo: [assistantNo, Validators.required],
      permission: [permission, Validators.required]
    });
    this.permissionsArray.push(permissionGroup);
  }

  // ========================
  // 組織管理相關方法
  // ========================

  // 切換節點展開/收合狀態
  toggleNode(node: OrganizationNode): void {
    if (node.type !== 'user') {
      node.expanded = !node.expanded;
    }
  }

  // 選擇部門
  selectDepartment(node: OrganizationNode): void {
    console.log('點擊了組織節點:', node.name, '類型:', node.type);

    if (node.type === 'department' || node.type === 'company') {
      this.selectedDepartment = node;
      console.log('已選擇部門:', this.selectedDepartment.name);

      // 清除之前的選擇狀態
      this.clearUserSelection();

      // 根據選擇的部門過濾右邊的用戶列表
      this.filterUsersByDepartment(node);
    } else {
      console.log('非部門或公司節點，不進行篩選');
    }
  }

  // 根據部門過濾用戶
  private filterUsersByDepartment(department: OrganizationNode): void {
    console.log('選擇的部門:', department.name);
    console.log('所有用戶數量:', this.users.length);
    console.log('所有用戶:', this.users.map(u => ({ id: u.id, username: u.username })));

    // 收集該部門及其子部門下所有使用者的ID
    const userIds = this.collectUserIdsFromNode(department);
    console.log('收集到的使用者ID:', userIds);

    // 根據ID過濾使用者列表
    if (userIds.length > 0) {
      this.filteredUsers = this.users.filter(user => {
        const isMatch = userIds.includes(user.id);
        console.log(`用戶 ${user.username} (${user.id}): ${isMatch ? '匹配' : '不匹配'}`);
        return isMatch;
      });
    } else {
      // 如果沒有使用者ID，顯示空列表
      this.filteredUsers = [];
      console.log('沒有找到使用者ID，顯示空列表');
    }

    console.log('過濾後的使用者數量:', this.filteredUsers.length);
    console.log('過濾後的使用者:', this.filteredUsers.map(u => ({ id: u.id, username: u.username })));
  }

  // 收集該部門及其所有子部門包含的用戶ID（遞歸收集）
  private collectUserIdsFromNode(node: OrganizationNode): string[] {
    const userIds: string[] = [];

    console.log(`正在處理節點: ${node.name} (類型: ${node.type})`);

    // 如果當前節點是使用者，直接收集其ID
    if (node.type === 'user' && node.id) {
      // 檢查用戶是否在實際用戶列表中存在
      const userExists = this.users.some(user => user.id === node.id);
      if (userExists) {
        console.log(`找到用戶: ${node.name} (ID: ${node.id})`);
        userIds.push(node.id);
      } else {
        console.warn(`跳過無效用戶 ID: ${node.id}`);
      }
    }

    // 遞歸處理所有子節點，包括子部門
    if (node.children && node.children.length > 0) {
      console.log(`節點 ${node.name} 有 ${node.children.length} 個子節點`);
      node.children.forEach(child => {
        if (child.type === 'user' && child.id) {
          // 收集直接的用戶子節點，但先檢查有效性
          const userExists = this.users.some(user => user.id === child.id);
          if (userExists) {
            console.log(`找到直接用戶: ${child.name} (ID: ${child.id})`);
            userIds.push(child.id);
          } else {
            console.warn(`跳過無效用戶 ID: ${child.id}`);
          }
        } else if (child.type === 'department') {
          // 遞歸處理子部門，收集其下所有用戶
          console.log(`遞歸處理子部門: ${child.name}`);
          const childUserIds = this.collectUserIdsFromNode(child);
          userIds.push(...childUserIds);
        }
      });
    } else {
      console.log(`節點 ${node.name} 沒有子節點`);
    }

    console.log(`節點 ${node.name} 收集到的用戶ID:`, userIds);
    return userIds;
  }

  // 重置篩選（顯示所有使用者）
  resetUserFilter(): void {
    this.selectedDepartment = null;
    this.filteredUsers = [...this.users];
    this.clearUserSelection(); // 清除選擇狀態
  }

  // 計算部門下的用戶數量
  getUserCountInDepartment(node: OrganizationNode): number {
    if (node.type === 'user') {
      return 0;
    }

    if (!node.children || node.children.length === 0) {
      return 0;
    }

    let count = 0;
    node.children.forEach(child => {
      if (child.type === 'user') {
        count++;
      } else if (child.type === 'department') {
        count += this.getUserCountInDepartment(child);
      }
    });

    return count;
  }

  // 計算部門直接下的用戶數量（不包含子部門）
  getDirectUserCountInDepartment(node: OrganizationNode): number {
    if (node.type === 'user') {
      return 0;
    }

    // 安全檢查 children 是否存在
    if (!node.children || node.children.length === 0) {
      return 0;
    }

    let count = 0;
    node.children.forEach(child => {
      if (child.type === 'user') {
        count++;
      }
    });

    return count;
  }

  // 根據展開狀態決定顯示的用戶數量
  getDisplayUserCount(node: OrganizationNode): number {
    if (node.expanded) {
      // 展開時顯示直接用戶數
      return this.getDirectUserCountInDepartment(node);
    } else {
      // 收合時顯示總用戶數（包含子部門）
      return this.getUserCountInDepartment(node);
    }
  }

  // 獲取節點圖示的 CSS 類別
  getNodeIconClass(node: OrganizationNode): string {
    if (node.type === 'company') {
      return 'fas fa-building';
    } else if (node.type === 'department') {
      return node.expanded ? 'fas fa-folder-open' : 'fas fa-folder';
    } else {
      return 'fas fa-user';
    }
  }

  // 檢查節點是否有子節點
  hasChildren(node: OrganizationNode): boolean {
    return !!(node.children && node.children.length > 0);
  }

  // 檢查節點是否有子部門（不是只有用戶）
  hasDepartmentChildren(node: OrganizationNode): boolean {
    if (!node.children || node.children.length === 0) {
      return false;
    }
    // 檢查是否有任何子節點是 department 類型
    return node.children.some(child => child.type === 'department');
  }

  // 檢查節點是否為非用戶節點且有部門子節點（可展開）
  isExpandable(node: OrganizationNode): boolean {
    return node.type !== 'user' && this.hasDepartmentChildren(node);
  }

  // ========================
  // 組織架構設定相關方法
  // ========================

  // 開啟組織設定彈出視窗
  openOrgSettingsModal(): void {
    this.showOrgSettingsModal = true;
    this.resetOrgForm();
  }

  // 關閉組織設定彈出視窗
  closeOrgSettingsModal(): void {
    this.showOrgSettingsModal = false;
    this.resetOrgForm();
  }

  // 重置組織表單
  resetOrgForm(): void {
    this.orgEditingNode = null;
    this.orgEditMode = null;
    this.orgFormData = {
      name: '',
      parentId: '',
      type: 'department'
    };
  }

  // 新增部門
  addDepartment(parentNode?: OrganizationNode): void {
    this.orgEditMode = 'add';
    this.orgEditingNode = parentNode || null;
    this.orgFormData = {
      name: '',
      parentId: parentNode?.id || '',
      type: 'department'
    };
  }

  // 編輯部門
  editDepartment(node: OrganizationNode): void {
    this.orgEditMode = 'edit';
    this.orgEditingNode = node;
    this.orgFormData = {
      name: node.name,
      parentId: '',
      type: node.type as 'department' | 'company'
    };
  }

  // 確認刪除部門
  confirmDeleteDepartment(node: OrganizationNode): void {
    if (confirm(`確定要刪除「${node.name}」嗎？此操作無法復原。`)) {
      this.deleteDepartment(node);
    }
  }

  // 刪除部門
  deleteDepartment(node: OrganizationNode): void {
    if (!this.organizationData) return;

    // 如果要刪除的是根節點
    if (this.organizationData.id === node.id) {
      alert(this.i18nService.translate('user_management.messages.cannot_delete_root_org'));
      return;
    }

    const companyid = environment.companyid;
    const assistantNo =  this.assistantNo;

    const url = `${environment.apiUrl}/api/departments/${companyid}/${assistantNo}/${node.id}`;

    console.log('刪除部門 API 呼叫，URL:', url);

    // 設定載入狀態
    this.isLoading = true;

    this.http.delete<ApiResponse<OrganizationNode>>(url).subscribe({
      next: (response) => {
        console.log('刪除部門 API 回應:', response);
        if (response.success && response.data) {
          // API 成功，更新本地組織資料
          this.organizationData = response.data;

          this.logService.Loginlog(companyid, 'UserManagement', 'admin', `部門已刪除: ${node.name}`, 'INFO');

          console.log('部門刪除成功');
        } else {
          console.error('刪除部門失敗:', response.error || '未知錯誤');
          const errorMsg = response.error || this.i18nService.translate('user_management.messages.unknown_error');
          alert(this.i18nService.translate('user_management.messages.delete_department_failed', { error: errorMsg }));
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('刪除部門 API 錯誤:', error);
        alert(this.i18nService.translate('user_management.messages.delete_department_failed_retry'));
        this.isLoading = false;
      }
    });
  }

  // 儲存組織變更
  saveOrgChanges(): void {
    if (!this.orgFormData.name.trim()) {
      alert(this.i18nService.translate('user_management.messages.department_name_required'));
      return;
    }

    // 設定載入狀態
    this.isLoading = true;

    if (this.orgEditMode === 'add') {
      this.executeAddDepartment();
    } else if (this.orgEditMode === 'edit') {
      this.executeEditDepartment();
    }
  }

  // 執行新增部門
  executeAddDepartment(): void {
    if (!this.organizationData) {
      this.isLoading = false;
      return;
    }

    const companyid = environment.companyid;
    const assistantNo = this.assistantNo;

    // 準備 API 呼叫的資料，將部門資訊包裝在 node 物件中
    const newDepartmentData = {
      companyid: companyid,
      assistantNo: assistantNo,
      parentId: this.orgEditingNode?.id || this.organizationData.id,
      node: {
        id: uuidv4(), // 生成 UUID
        name: this.orgFormData.name,
        type: "department"
      }
    };

    const url = `${environment.apiUrl}/api/departments`;

    console.log('新增部門 API 呼叫，URL:', url, '資料:', newDepartmentData);

    this.http.post<ApiResponse<OrganizationNode>>(url, newDepartmentData).subscribe({
      next: (response) => {
        console.log('新增部門 API 回應:', response);
        if (response.success && response.data) {
          // API 成功，更新本地組織資料
          this.organizationData = response.data;

          this.logService.Loginlog(companyid, 'UserManagement', 'admin', `新部門已新增: ${this.orgFormData.name}`, 'INFO');

          // 重置表單並關閉編輯模式
          this.resetOrgForm();

          console.log('部門新增成功');
        } else {
          console.error('新增部門失敗:', response.error || '未知錯誤');
          const errorMsg = response.error || this.i18nService.translate('user_management.messages.unknown_error');
          alert(this.i18nService.translate('user_management.messages.add_department_failed', { error: errorMsg }));
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('新增部門 API 錯誤:', error);
        alert(this.i18nService.translate('user_management.messages.add_department_failed_retry'));
        this.isLoading = false;
      }
    });
  }

  // 執行編輯部門
  executeEditDepartment(): void {
    if (!this.orgEditingNode) {
      this.isLoading = false;
      return;
    }

    const companyid = environment.companyid;
    const assistantNo = this.assistantNo;

    // 準備更新資料，按照後端 API 要求的格式包裝在 updates 物件中
    const updateData = {
      updates: {
        name: this.orgFormData.name
      }
    };

    const url = `${environment.apiUrl}/api/departments/${companyid}/${assistantNo}/${this.orgEditingNode.id}`;

    console.log('更新部門 API 呼叫，URL:', url, '資料:', updateData);

    this.http.put<ApiResponse<OrganizationNode>>(url, updateData).subscribe({
      next: (response) => {
        console.log('更新部門 API 回應:', response);
        if (response.success && response.data) {
          // API 成功，更新本地組織資料
          this.organizationData = response.data;

          this.logService.Loginlog(companyid, 'UserManagement', 'admin', `部門名稱已更新: ${this.orgFormData.name}`, 'INFO');

          // 重置表單並關閉編輯模式
          this.resetOrgForm();

          console.log('部門更新成功');
        } else {
          console.error('更新部門失敗:', response.error || '未知錯誤');
          const errorMsg = response.error || this.i18nService.translate('user_management.messages.unknown_error');
          alert(this.i18nService.translate('user_management.messages.update_department_failed', { error: errorMsg }));
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('更新部門 API 錯誤:', error);
        alert(this.i18nService.translate('user_management.messages.update_department_failed_retry'));
        this.isLoading = false;
      }
    });
  }

  // 生成唯一 ID (UUID 格式)
  generateUniqueId(): string {
    return uuidv4();
  }

  // 取得所有部門列表（用於父部門選擇）
  getAllDepartments(node: OrganizationNode = this.organizationData!): OrganizationNode[] {
    if (!node) return [];

    let departments: OrganizationNode[] = [];

    if (node.type === 'department' || node.type === 'company') {
      departments.push(node);
    }

    // 安全檢查 children 是否存在
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        if (child.type === 'department') {
          departments = departments.concat(this.getAllDepartments(child));
        }
      });
    }

    return departments;
  }

  // 取得帶有層級資訊的部門列表（用於父部門選擇下拉選單）
  getDepartmentsWithLevel(node: OrganizationNode = this.organizationData!, level: number = 0): DepartmentWithLevel[] {
    if (!node) return [];

    let departments: DepartmentWithLevel[] = [];

    if (node.type === 'department' || node.type === 'company') {
      // 為不同層級添加縮排空格，每一層級使用 4 個全形空格
      const indentSpaces = '\u3000'.repeat(level); // 使用全形空格 (U+3000) 來確保縮排效果明顯
      const displayName = indentSpaces + node.name;
      
      departments.push({
        id: node.id,
        name: displayName,
        level: level,
        type: node.type
      });
    }

    // 安全檢查 children 是否存在
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        if (child.type === 'department') {
          departments = departments.concat(this.getDepartmentsWithLevel(child, level + 1));
        }
      });
    }

    return departments;
  }

  // 上移部門
  moveDepartmentUp(node: OrganizationNode): void {
    if (!this.organizationData) return;

    const parent = this.findParentNode(this.organizationData, node.id);
    if (!parent || !parent.children || parent.children.length === 0) return;

    const index = parent.children.findIndex(child => child.id === node.id);
    if (index > 0) {
      // 設定視覺反饋
      node.isMoving = true;

      [parent.children[index], parent.children[index - 1]] =
      [parent.children[index - 1], parent.children[index]];

      // 短暫高亮顯示後恢復
      setTimeout(() => {
        if (node) {
          node.isMoving = false;
        }
      }, 1000);
    }
  }

  // 下移部門
  moveDepartmentDown(node: OrganizationNode): void {
    if (!this.organizationData) return;

    const parent = this.findParentNode(this.organizationData, node.id);
    if (!parent || !parent.children || parent.children.length === 0) return;

    const index = parent.children.findIndex(child => child.id === node.id);
    if (index >= 0 && index < parent.children.length - 1) {
      // 設定視覺反饋
      node.isMoving = true;

      [parent.children[index], parent.children[index + 1]] =
      [parent.children[index + 1], parent.children[index]];

      // 短暫高亮顯示後恢復
      setTimeout(() => {
        if (node) {
          node.isMoving = false;
        }
      }, 1000);
    }
  }

  // 查找節點的父節點
  findParentNode(root: OrganizationNode, targetId: string): OrganizationNode | null {
    // 安全檢查 children 是否存在
    if (!root.children || root.children.length === 0) {
      return null;
    }

    for (const child of root.children) {
      if (child.id === targetId) {
        return root;
      }
      const found = this.findParentNode(child, targetId);
      if (found) return found;
    }
    return null;
  }  // 獲取用戶所屬的組織單位（只返回最後一層部門名稱）
  getUserOrganizationUnits(user: User): string[] {
    if (!this.organizationData) return [];

    const organizationUnits: string[] = [];

    // 遞歸搜尋用戶在組織架構中的位置
    const findUserInOrganization = (node: OrganizationNode, parentPath: string[] = []): void => {
      if (node.type === 'user' && node.id === user.id) {
        // 找到用戶，只記錄最後一層部門名稱
        if (parentPath.length > 0) {
          const lastDepartment = parentPath[parentPath.length - 1];
          if (lastDepartment && !organizationUnits.includes(lastDepartment)) {
            organizationUnits.push(lastDepartment);
          }
        }
      } else if (node.children && node.children.length > 0) {
        // 繼續搜尋子節點，但只記錄部門層級
        const currentPath = node.type === 'department' ? [...parentPath, node.name] : parentPath;
        node.children.forEach(child => {
          findUserInOrganization(child, currentPath);
        });
      }
    };

    findUserInOrganization(this.organizationData);

    // 如果在組織架構中找不到，則使用用戶資料中的 department 字段
    if (organizationUnits.length === 0 && user.department) {
      organizationUnits.push(user.department);
    }

    return organizationUnits;
  }

  // 獲取組織單位標籤陣列（用於顯示標籤）
  getOrganizationUnitTags(user: User): string[] {
    const units = this.getUserOrganizationUnits(user);
    return units.length > 0 ? units : ['未分配'];
  }

  // ==================== 部門成員管理功能 ====================

  // 開啟部門成員管理模態框
  openDepartmentMemberModal(): void {
    console.log('嘗試開啟部門成員管理模態框');
    console.log('選中的部門:', this.selectedDepartment);

    if (!this.selectedDepartment) {
      console.log('沒有選中的部門，無法開啟模態框');
      return;
    }

    console.log('設定 showDepartmentMemberModal = true');
    this.showDepartmentMemberModal = true;
    this.resetModalSearchState();

    console.log('部門成員管理模態框應該已開啟，showDepartmentMemberModal:', this.showDepartmentMemberModal);
  }

  // 關閉部門成員管理模態框
  closeDepartmentMemberModal(): void {
    this.showDepartmentMemberModal = false;
    this.resetModalSearchState();
  }

  // 重設選擇狀態
  private resetSearchState(): void {
    this.selectedUserToAdd = '';
    this.selectedUsersToAdd = [];
    this.userFilterType = 'all'; // 重設篩選類型為顯示所有用戶
    this.clearUserSelection(); // 清除選擇狀態
  }

  // 重設模態框搜索狀態（不影響部門選擇）
  private resetModalSearchState(): void {
    this.selectedUserToAdd = '';
    this.selectedUsersToAdd = [];
    this.userFilterType = 'all'; // 重設篩選類型為顯示所有用戶
    // 注意：不調用 clearUserSelection()，因為那會影響部門的選擇狀態
  }

  // 下拉選單變更事件
  onUserDropdownChange(): void {
    if (!this.selectedUserToAdd) {
      return;
    }

    const selectedUser = this.users.find(user => user.id === this.selectedUserToAdd);
    if (!selectedUser) {
      return;
    }

    // 檢查是否已在當前部門或已在選擇列表中
    if (this.isUserAlreadyInDepartment(selectedUser)) {
      alert(this.i18nService.translate('user_management.messages.user_already_in_department'));
      this.selectedUserToAdd = '';
      return;
    }

    if (this.isUserInSelectedList(selectedUser)) {
      alert(this.i18nService.translate('user_management.messages.user_already_selected'));
      this.selectedUserToAdd = '';
      return;
    }

    // 添加到選擇列表
    this.selectedUsersToAdd.push(selectedUser);
    console.log('已添加用戶到選擇列表:', selectedUser);

    // 重置下拉選單
    this.selectedUserToAdd = '';
  }

  // 取得分組的用戶列表
  getGroupedUsers(): Array<{label: string, users: User[]}> {
    // 按字母順序分組用戶
    const groups: Array<{label: string, users: User[]}> = [];

    let usersToShow: User[] = [];

    // 根據篩選類型決定顯示哪些用戶
    if (this.userFilterType === 'noDepartment') {
      // 僅顯示未屬於任何部門的用戶
      usersToShow = this.users.filter(user => {
        const orgUnits = this.getUserOrganizationUnits(user);
        return orgUnits.length === 0;
      });
    } else {
      // 顯示所有用戶
      usersToShow = [...this.users];
    }

    // 可用的用戶（未在當前部門且未被選擇）
    const availableUsers = usersToShow.filter(user =>
      !this.isUserAlreadyInDepartment(user) && !this.isUserInSelectedList(user)
    );

    // 已在部門的用戶
    const usersInDepartment = usersToShow.filter(user =>
      this.isUserAlreadyInDepartment(user)
    );

    // 已選擇的用戶
    const selectedUsers = usersToShow.filter(user =>
      this.isUserInSelectedList(user)
    );

    if (availableUsers.length > 0) {
      groups.push({
        label: `${this.i18nService.translate('user_management.department_member.available_users')} (${availableUsers.length})`,
        users: availableUsers.sort((a, b) => (a.fullName || a.username).localeCompare(b.fullName || b.username))
      });
    }

    if (usersInDepartment.length > 0) {
      groups.push({
        label: `${this.i18nService.translate('user_management.department_member.users_in_department')} (${usersInDepartment.length})`,
        users: usersInDepartment.sort((a, b) => (a.fullName || a.username).localeCompare(b.fullName || b.username))
      });
    }

    if (selectedUsers.length > 0) {
      groups.push({
        label: `${this.i18nService.translate('user_management.department_member.selected_users')} (${selectedUsers.length})`,
        users: selectedUsers.sort((a, b) => (a.fullName || a.username).localeCompare(b.fullName || b.username))
      });
    }

    return groups;
  }

  // 取得可用用戶數量
  getAvailableUsersCount(): number {
    return this.users.filter(user =>
      !this.isUserAlreadyInDepartment(user) && !this.isUserInSelectedList(user)
    ).length;
  }

  // 檢查用戶是否已在選擇列表中
  isUserInSelectedList(user: User): boolean {
    return this.selectedUsersToAdd.some(selectedUser => selectedUser.id === user.id);
  }

  // 檢查用戶是否已經在當前部門
  isUserAlreadyInDepartment(user: User): boolean {
    if (!this.selectedDepartment) {
      return false;
    }

    const userOrgUnits = this.getUserOrganizationUnits(user);
    return userOrgUnits.some(orgUnit => orgUnit === this.selectedDepartment!.name);
  }

  // 從選擇列表中移除用戶
  removeFromSelectedList(index: number): void {
    if (index >= 0 && index < this.selectedUsersToAdd.length) {
      const removedUser = this.selectedUsersToAdd.splice(index, 1)[0];
      console.log('已從選擇列表中移除用戶:', removedUser);
    }
  }

  // 確認添加選中的用戶到部門
  confirmAddUsersToSelectedDepartment(): void {
    if (!this.selectedDepartment || this.selectedUsersToAdd.length === 0) {
      return;
    }

    console.log(`準備將 ${this.selectedUsersToAdd.length} 位用戶添加到部門 ${this.selectedDepartment.name}`);

    const companyid = environment.companyid;
    const assistantNo = this.assistantNo;

    // 保存組織架構的展開狀態
    const expandedStates = this.organizationData ? this.saveOrganizationExpandedStates(this.organizationData) : new Map<string, boolean>();

    // 設定載入狀態
    this.isLoading = true;

    // 準備批量添加用戶的資料，將所有用戶節點放在一起
    const batchAddUserData = {
      companyid: companyid,
      assistantNo: assistantNo,
      parentId: this.selectedDepartment.id,
      nodes: this.selectedUsersToAdd.map(user => ({
        id: user.id,
        type: 'user' as const
      }))
    };

    const url = `${environment.apiUrl}/api/departments`;
    console.log('批量新增用戶到部門 API 呼叫，URL:', url, '資料:', batchAddUserData);

    // 執行單一 API 請求
    this.http.post<ApiResponse<OrganizationNode>>(url, batchAddUserData).subscribe({
      next: (response) => {
        console.log('批量添加用戶 API 回應:', response);

        if (response.success && response.data) {
          // 保存當前選擇的部門狀態
          const currentSelectedDepartment = this.selectedDepartment;

          // 更新本地組織資料
          this.organizationData = response.data;
          // 補充組織架構中用戶的完整資訊
          this.enrichOrganizationWithUserInfo(this.organizationData);
          // 恢復組織架構的展開狀態
          this.restoreOrganizationExpandedStates(this.organizationData, expandedStates);

          // 記錄成功操作
          if (this.selectedDepartment) {
            this.logService.Loginlog(
              companyid,
              'UserManagement',
              'admin',
              `批量添加 ${this.selectedUsersToAdd.length} 位用戶到部門: ${this.selectedDepartment.name}`,
              'INFO'
            );
          }

          // 重新過濾用戶列表以反映變更，但保持原本選擇的部門狀態
          if (currentSelectedDepartment) {
            // 在更新後的組織資料中找到對應的部門節點
            const updatedSelectedDepartment = this.findDepartmentInOrganization(currentSelectedDepartment.id, this.organizationData);
            if (updatedSelectedDepartment) {
              this.selectedDepartment = updatedSelectedDepartment;
              this.filterUsersByDepartment(updatedSelectedDepartment);
            }
          }

          // 顯示成功訊息
          console.log(`成功添加 ${this.selectedUsersToAdd.length} 位用戶到部門`);

          // 關閉模態框，但不重置部門選擇狀態
          this.showDepartmentMemberModal = false;
          this.resetModalSearchState();
        } else {
          console.error('批量添加用戶操作失敗:', response.error || '未知錯誤');
          const errorMsg = response.error || this.i18nService.translate('user_management.messages.unknown_error');
          alert(this.i18nService.translate('user_management.messages.batch_add_users_failed', { error: errorMsg }));
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('批量添加用戶 API 錯誤:', error);
        alert(this.i18nService.translate('user_management.messages.add_users_to_department_failed'));
        this.isLoading = false;
      }
    });
  }

  // 清空所有選擇的用戶
  clearAllSelectedUsers(): void {
    this.selectedUsersToAdd = [];
    console.log('已清空所有選擇的用戶');
  }

  // 將用戶添加到組織節點中（輔助方法）
  private addUserToOrganizationNode(user: User, departmentNode: OrganizationNode): void {
    // 初始化 children 如果不存在
    if (!departmentNode.children) {
      departmentNode.children = [];
    }

    // 創建用戶節點
    const userNode: OrganizationNode = {
      id: user.id || '',
      name: user.fullName || user.username || '',
      type: 'user',
      children: [],
      companyid: user.companyid || environment.companyid
    };

    // 檢查是否已存在，避免重複添加
    const existingUser = departmentNode.children.find(child =>
      child.type === 'user' && child.id === user.id
    );

    if (!existingUser) {
      departmentNode.children.push(userNode);
    }
  }

  // 從組織節點中移除用戶（輔助方法）
  private removeUserFromOrganizationNode(user: User, departmentNode: OrganizationNode): void {
    // 安全檢查 children 是否存在
    if (!departmentNode.children) {
      return;
    }

    departmentNode.children = departmentNode.children.filter(child =>
      !(child.type === 'user' && child.id === user.id)
    );
  }

  // 在組織架構中找到對應的部門節點（輔助方法）
  private findDepartmentInOrganization(departmentId: string, organizationRoot: OrganizationNode | null): OrganizationNode | null {
    if (!organizationRoot) {
      return null;
    }

    // 檢查當前節點是否為目標部門
    if (organizationRoot.type === 'department' && organizationRoot.id === departmentId) {
      return organizationRoot;
    }

    // 遞歸搜尋子節點
    if (organizationRoot.children && organizationRoot.children.length > 0) {
      for (const child of organizationRoot.children) {
        const found = this.findDepartmentInOrganization(departmentId, child);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  // 保存組織架構的展開狀態
  private saveOrganizationExpandedStates(node: OrganizationNode): Map<string, boolean> {
    const expandedStates = new Map<string, boolean>();

    const collectExpandedStates = (currentNode: OrganizationNode): void => {
      if (currentNode.id && currentNode.expanded !== undefined) {
        expandedStates.set(currentNode.id, currentNode.expanded);
      }

      if (currentNode.children && currentNode.children.length > 0) {
        currentNode.children.forEach(child => {
          collectExpandedStates(child);
        });
      }
    };

    collectExpandedStates(node);
    return expandedStates;
  }

  // 恢復組織架構的展開狀態
  private restoreOrganizationExpandedStates(node: OrganizationNode, expandedStates: Map<string, boolean>): void {
    const restoreStates = (currentNode: OrganizationNode): void => {
      if (currentNode.id && expandedStates.has(currentNode.id)) {
        currentNode.expanded = expandedStates.get(currentNode.id);
      }

      if (currentNode.children && currentNode.children.length > 0) {
        currentNode.children.forEach(child => {
          restoreStates(child);
        });
      }
    };

    restoreStates(node);
  }

  // 用戶篩選類型變更處理
  onUserFilterTypeChange(): void {
    // 重設選擇狀態
    this.selectedUserToAdd = '';
    // 已選擇的用戶列表不需要清空，因為用戶可能想保留已選擇的用戶
  }

  // ========================
  // 使用者選擇和移除功能方法
  // ========================

  // 處理單一用戶複選框狀態變更
  onUserCheckboxChange(userId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    if (isChecked) {
      this.selectedUsersForRemoval.add(userId);
    } else {
      this.selectedUsersForRemoval.delete(userId);
      this.isSelectAllChecked = false; // 取消任何一個選擇時，全選狀態設為false
    }
    this.updateSelectAllState();
  }

  // 處理全選複選框狀態變更
  onSelectAllChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    this.isSelectAllChecked = isChecked;
    if (isChecked) {
      // 選取所有部門內的用戶
      this.filteredUsers.forEach(user => {
        this.selectedUsersForRemoval.add(user.id);
      });
    } else {
      // 清除所有選擇
      this.selectedUsersForRemoval.clear();
    }
  }

  // 更新全選狀態
  private updateSelectAllState(): void {
    const currentDeptUserIds = this.filteredUsers.map(user => user.id);
    const selectedFromCurrentDept = currentDeptUserIds.filter(id =>
      this.selectedUsersForRemoval.has(id)
    );
    this.isSelectAllChecked = currentDeptUserIds.length > 0 &&
                              selectedFromCurrentDept.length === currentDeptUserIds.length;
  }

  // 檢查全選複選框是否應該為半選狀態
  isSelectAllIndeterminate(): boolean {
    const currentDeptUserIds = this.filteredUsers.map(user => user.id);
    const selectedFromCurrentDept = currentDeptUserIds.filter(id =>
      this.selectedUsersForRemoval.has(id)
    );

    return selectedFromCurrentDept.length > 0 &&
           selectedFromCurrentDept.length < currentDeptUserIds.length;
  }

  // 檢查特定用戶是否被選中
  isUserSelected(userId: string): boolean {
    return this.selectedUsersForRemoval.has(userId);
  }

  // 取得選中用戶數量
  getSelectedUsersCount(): number {
    return this.selectedUsersForRemoval.size;
  }

  // 從當前部門移除選中的用戶
  removeSelectedUsersFromDepartment(): void {
    if (!this.selectedDepartment || this.selectedUsersForRemoval.size === 0) {
      return;
    }

    const selectedUserIds = Array.from(this.selectedUsersForRemoval);
    const usersToRemove = this.users.filter(user =>
      selectedUserIds.includes(user.id)
    );

    if (usersToRemove.length === 0) {
      return;
    }

    // 確認對話框
    const userNames = usersToRemove.map(user => user.fullName || user.username).join(', ');
    const confirmed = confirm(
      `確定要將以下 ${usersToRemove.length} 位用戶從「${this.selectedDepartment.name}」部門移除嗎？\n\n${userNames}`
    );

    if (confirmed) {
      const companyid = environment.companyid;
      const assistantNo = this.assistantNo;

      // 保存組織架構的展開狀態
      const expandedStates = this.organizationData ? this.saveOrganizationExpandedStates(this.organizationData) : new Map<string, boolean>();

      // 設定載入狀態
      this.isLoading = true;

      // 準備批量 DELETE API 請求
      const removeUserRequests = usersToRemove.map(user => {
        const url = `${environment.apiUrl}/api/departments/${companyid}/${assistantNo}/${this.selectedDepartment!.id}/${user.id}`;
        console.log(`從部門移除用戶 API 呼叫，URL: ${url}，用戶: ${user.fullName || user.username}`);

        return this.http.delete<ApiResponse<OrganizationNode>>(url);
      });

      // 並行執行所有 DELETE API 請求
      forkJoin(removeUserRequests).subscribe({
        next: (responses) => {
          console.log('批量移除用戶 API 回應:', responses);

          // 檢查所有回應是否成功
          const allSuccessful = responses.every(response => response.success);

          if (allSuccessful) {
            // 保存當前選擇的部門狀態
            const currentSelectedDepartment = this.selectedDepartment;

            // 使用最後一個回應的資料更新本地組織資料
            const lastResponse = responses[responses.length - 1];
            if (lastResponse.data) {
              this.organizationData = lastResponse.data;
              // 補充組織架構中用戶的完整資訊
              this.enrichOrganizationWithUserInfo(this.organizationData);
              // 恢復組織架構的展開狀態
              this.restoreOrganizationExpandedStates(this.organizationData, expandedStates);
            }

            // 記錄成功操作
            if (this.selectedDepartment) {
              this.logService.Loginlog(
                companyid,
                'UserManagement',
                'admin',
                `批量移除 ${usersToRemove.length} 位用戶從部門: ${this.selectedDepartment.name}`,
                'INFO'
              );
            }

            // 重新過濾用戶列表，但保持原本選擇的部門狀態
            if (currentSelectedDepartment) {
              // 在更新後的組織資料中找到對應的部門節點
              const updatedSelectedDepartment = this.findDepartmentInOrganization(currentSelectedDepartment.id, this.organizationData);
              if (updatedSelectedDepartment) {
                this.selectedDepartment = updatedSelectedDepartment;
                this.filterUsersByDepartment(updatedSelectedDepartment);
              }
            }

            // 清除選擇狀態
            this.clearUserSelection();

            if (this.selectedDepartment) {
              console.log(`已將 ${usersToRemove.length} 位用戶從部門 ${this.selectedDepartment.name} 移除`);
            }
          } else {
            console.error('部分或全部移除用戶操作失敗');
            alert(this.i18nService.translate('user_management.messages.remove_users_partially_failed'));
          }

          this.isLoading = false;
        },
        error: (error) => {
          console.error('批量移除用戶 API 錯誤:', error);
          alert(this.i18nService.translate('user_management.messages.remove_users_from_department_failed'));
          this.isLoading = false;
        }
      });
    }
  }

  // 清除用戶選擇狀態
  private clearUserSelection(): void {
    this.selectedUsersForRemoval.clear();
    this.isSelectAllChecked = false;
  }

  // 新增：切換組織架構面板收合狀態
  toggleOrgPanel(): void {
    this.isOrgPanelCollapsed = !this.isOrgPanelCollapsed;
  }

  // 新增：處理組織管理樹節點事件
  onOrgNodeMoveUp(node: OrganizationNode): void {
    this.moveDepartmentUp(node);
  }

  onOrgNodeMoveDown(node: OrganizationNode): void {
    this.moveDepartmentDown(node);
  }

  onOrgNodeEdit(node: OrganizationNode): void {
    this.editDepartment(node);
  }

  onOrgNodeAddChild(node: OrganizationNode): void {
    this.addDepartment(node);
  }

  onOrgNodeDelete(node: OrganizationNode): void {
    this.confirmDeleteDepartment(node);
  }

  onOrgNodeToggleExpanded(node: OrganizationNode): void {
    // 已在組件內部處理展開/收合狀態
    // 這裡可以添加額外的邏輯，如記錄狀態等
  }

  // 初始化組織樹節點的展開狀態
  private initializeOrgNodeExpandedStates(node: OrganizationNode): void {
    if (!node) return;

    // 默認展開第一層和第二層
    if (node.type === 'company') {
      node.expanded = true;
    } else {
      node.expanded = true; // 可以根據需要調整默認展開層級
    }

    // 遞歸處理子節點
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        this.initializeOrgNodeExpandedStates(child);
      });
    }
  }

  // 新增數據清理方法
  private cleanupOrganizationData(): void {
    if (!this.organizationData || !this.users) return;

    const validUserIds = new Set(this.users.map(user => user.id));

    this.organizationData = this.removeInvalidUsers(this.organizationData, validUserIds);
  }

  // 遞歸移除無效用戶
  private removeInvalidUsers(node: OrganizationNode, validUserIds: Set<string>): OrganizationNode {
    if (!node) return node;

    const cleanedNode = { ...node };

    if (cleanedNode.children) {
      cleanedNode.children = cleanedNode.children.map((child: OrganizationNode) => {
        if (child.type === 'user') {
          return validUserIds.has(child.id) ? child : null;
        } else if (child.type === 'department') {
          return this.removeInvalidUsers(child, validUserIds);
        }
        return child;
      }).filter(child => child !== null) as OrganizationNode[];
    }

    return cleanedNode;
  }

  // 新增數據一致性驗證方法
  private validateDataConsistency(): void {
    if (!this.filteredUsers || !this.organizationData) return;

    // 收集組織架構中的所有用戶 ID
    const orgUserIds = new Set<string>();
    this.collectAllUserIds(this.organizationData, orgUserIds);

    // 收集實際用戶列表的 ID
    const actualUserIds = new Set(this.filteredUsers.map(user => user.id));

    // 找出差異
    const invalidOrgUsers = Array.from(orgUserIds).filter(id => !actualUserIds.has(id));
    const unassignedUsers = Array.from(actualUserIds).filter(id => !orgUserIds.has(id));

    if (invalidOrgUsers.length > 0) {
      console.warn('組織架構中的無效用戶 ID:', invalidOrgUsers);
    }

    if (unassignedUsers.length > 0) {
      console.info('未分配到任何部門的用戶:', unassignedUsers);
    }

    console.log('數據一致性驗證完成');
  }

  // 收集組織架構中的所有用戶 ID
  private collectAllUserIds(node: OrganizationNode, userIds: Set<string>): void {
    if (!node) return;

    if (node.type === 'user' && node.id) {
      userIds.add(node.id);
    }

    if (node.children) {
      node.children.forEach(child => {
        this.collectAllUserIds(child, userIds);
      });
    }
  }
}
